import * as React from "react"

const MOBILE_BREAKPOINT = 768
const TABLET_BREAKPOINT = 1024

// Optimized mobile hook with reduced DOM queries and better performance
export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let rafId: number | null = null;
    
    const updateMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }

    updateMobile()

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(updateMobile)
      })
      resizeObserver.observe(document.documentElement)
    } else {
      let timeoutId: NodeJS.Timeout
      const throttledResize = () => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(updateMobile, 16)
      }
      window.addEventListener('resize', throttledResize, { passive: true })
      
      return () => {
        window.removeEventListener('resize', throttledResize)
        clearTimeout(timeoutId)
      }
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null;
      }
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    }
  }, [])

  return !!isMobile
}

export function useBreakpoint() {
  const [breakpoint, setBreakpoint] = React.useState<'mobile' | 'tablet' | 'desktop'>('desktop')

  React.useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let rafId: number | null = null;
    
    const updateBreakpoint = () => {
      const width = window.innerWidth
      if (width < MOBILE_BREAKPOINT) {
        setBreakpoint('mobile')
      } else if (width < TABLET_BREAKPOINT) {
        setBreakpoint('tablet')
      } else {
        setBreakpoint('desktop')
      }
    }

    updateBreakpoint()

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(updateBreakpoint)
      })
      resizeObserver.observe(document.documentElement)
    } else {
      let timeoutId: NodeJS.Timeout
      const throttledResize = () => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(updateBreakpoint, 16)
      }
      window.addEventListener('resize', throttledResize, { passive: true })
      
      return () => {
        window.removeEventListener('resize', throttledResize)
        clearTimeout(timeoutId)
      }
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null;
      }
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    }
  }, [])

  return React.useMemo(() => ({
    breakpoint,
    isMobile: breakpoint === 'mobile',
    isTablet: breakpoint === 'tablet', 
    isDesktop: breakpoint === 'desktop'
  }), [breakpoint])
}

// Hook for detecting device capabilities
export function useDeviceCapabilities() {
  return React.useMemo(() => ({
    isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    supportsHover: window.matchMedia('(hover: hover)').matches,
    isHighDensity: window.devicePixelRatio > 1.5,
    supportsWebGL: (() => {
      try {
        const canvas = document.createElement('canvas')
        return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
      } catch {
        return false
      }
    })(),
    supportsOfflineStorage: 'serviceWorker' in navigator && 'caches' in window
  }), [])
}

// Hook for optimized viewport management
export function useViewport() {
  const [viewport, setViewport] = React.useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
    scrollY: typeof window !== 'undefined' ? window.scrollY : 0
  })

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout
    let rafId: number

    const updateViewport = () => {
      rafId = requestAnimationFrame(() => {
        setViewport({
          width: window.innerWidth,
          height: window.innerHeight,
          scrollY: window.scrollY
        })
      })
    }

    const throttledUpdate = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(updateViewport, 16)
    }

    window.addEventListener('resize', throttledUpdate, { passive: true })
    window.addEventListener('scroll', throttledUpdate, { passive: true })

    return () => {
      window.removeEventListener('resize', throttledUpdate)
      window.removeEventListener('scroll', throttledUpdate)
      clearTimeout(timeoutId)
      cancelAnimationFrame(rafId)
    }
  }, [])

  return viewport
}